import React, { useEffect, useState, useCallback } from 'react';
import {
  getComplianceStats,
  getConsentRecords,
  addConsent,
  getDncList,
  addToDNC,
  checkDNC,
  getAuditLog,
  type ConsentRecord,
  type DncEntry,
  type ComplianceEvent,
  type ComplianceStats,
} from '../../api/compliance';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmt(dateStr?: string) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function fmtTime(dateStr?: string) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function consentStatus(r: ConsentRecord): { label: string; color: string } {
  if (r.revoked_at) return { label: 'Revoked', color: '#ef4444' };
  if (new Date(r.expires_at) < new Date()) return { label: 'Expired', color: '#f59e0b' };
  return { label: 'Valid', color: '#22c55e' };
}

const EVENT_LABELS: Record<string, string> = {
  consent_recorded: 'Consent Recorded',
  dnc_added: 'DNC Added',
  call_blocked: 'Call Blocked',
  opt_out_received: 'Opt-out Received',
  quiet_hours_blocked: 'Quiet Hours Blocked',
  call_initiated: 'Call Initiated',
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const Card: React.FC<{
  label: string;
  value: string | number;
  icon: string;
  accent: string;
  sub?: string;
}> = ({ label, value, icon, accent, sub }) => (
  <div
    className="rounded-xl p-5 flex flex-col gap-2"
    style={{ background: '#13131f', border: `1px solid ${accent}30` }}
  >
    <div className="flex items-center gap-2 text-sm" style={{ color: '#9ca3af' }}>
      <span>{icon}</span>
      <span>{label}</span>
    </div>
    <div className="text-3xl font-bold" style={{ color: accent }}>{value}</div>
    {sub && <div className="text-xs" style={{ color: '#6b7280' }}>{sub}</div>}
  </div>
);

const Tab: React.FC<{ label: string; active: boolean; onClick: () => void; badge?: string }> = ({
  label, active, onClick, badge
}) => (
  <button
    onClick={onClick}
    className="px-4 py-2 text-sm font-medium rounded-lg transition-all"
    style={{
      background: active ? 'rgba(99,102,241,0.15)' : 'transparent',
      color: active ? '#818cf8' : '#9ca3af',
      border: active ? '1px solid rgba(99,102,241,0.3)' : '1px solid transparent',
    }}
  >
    {label}
    {badge && (
      <span
        className="ml-2 text-xs px-1.5 py-0.5 rounded-full"
        style={{ background: '#dc2626', color: '#fff' }}
      >
        {badge}
      </span>
    )}
  </button>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

type ActiveTab = 'consent' | 'dnc' | 'audit' | 'settings';

export const ComplianceCenter: React.FC = () => {
  const [tab, setTab] = useState<ActiveTab>('consent');
  const [stats, setStats] = useState<ComplianceStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  // Consent tab state
  const [consentRecords, setConsentRecords] = useState<ConsentRecord[]>([]);
  const [consentTotal, setConsentTotal] = useState(0);
  const [consentPage, setConsentPage] = useState(1);
  const [consentLoading, setConsentLoading] = useState(false);
  const [showAddConsent, setShowAddConsent] = useState(false);
  const [addConsentForm, setAddConsentForm] = useState({
    phone: '', consentMethod: 'web_form' as const, consentText: '', recordingUrl: '',
  });
  const [addConsentLoading, setAddConsentLoading] = useState(false);
  const [addConsentError, setAddConsentError] = useState('');

  // DNC tab state
  const [dncEntries, setDncEntries] = useState<DncEntry[]>([]);
  const [dncTotal, setDncTotal] = useState(0);
  const [dncPage, setDncPage] = useState(1);
  const [dncLoading, setDncLoading] = useState(false);
  const [showAddDnc, setShowAddDnc] = useState(false);
  const [addDncForm, setAddDncForm] = useState({ phone: '', reason: 'manual' as const });
  const [checkDncPhone, setCheckDncPhone] = useState('');
  const [checkDncResult, setCheckDncResult] = useState<{ onDNC: boolean; reason?: string } | null>(null);

  // Audit log state
  const [events, setEvents] = useState<ComplianceEvent[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditEventFilter, setAuditEventFilter] = useState('');

  // Settings state
  const [settings, setSettings] = useState({
    quietHoursEnabled: true,
    consentRequired: true,
    aiDisclosure: "Hi, this is an AI calling on behalf of [Company]. I'm here to...",
    optOutKeywords: 'stop, remove me, don\'t call, opt out, unsubscribe',
  });

  // Load stats
  useEffect(() => {
    setStatsLoading(true);
    getComplianceStats()
      .then(setStats)
      .catch(console.error)
      .finally(() => setStatsLoading(false));
  }, []);

  // Load consent records
  const loadConsent = useCallback(async () => {
    setConsentLoading(true);
    try {
      const result = await getConsentRecords(consentPage);
      setConsentRecords(result.records);
      setConsentTotal(result.total);
    } catch (err) { console.error(err); }
    finally { setConsentLoading(false); }
  }, [consentPage]);

  useEffect(() => { if (tab === 'consent') loadConsent(); }, [tab, consentPage, loadConsent]);

  // Load DNC
  const loadDNC = useCallback(async () => {
    setDncLoading(true);
    try {
      const result = await getDncList(dncPage);
      setDncEntries(result.entries);
      setDncTotal(result.total);
    } catch (err) { console.error(err); }
    finally { setDncLoading(false); }
  }, [dncPage]);

  useEffect(() => { if (tab === 'dnc') loadDNC(); }, [tab, dncPage, loadDNC]);

  // Load audit log
  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const params: Parameters<typeof getAuditLog>[0] = { page: auditPage };
      if (auditEventFilter) params.eventType = auditEventFilter;
      const result = await getAuditLog(params);
      setEvents(result.events);
      setAuditTotal(result.total);
    } catch (err) { console.error(err); }
    finally { setAuditLoading(false); }
  }, [auditPage, auditEventFilter]);

  useEffect(() => { if (tab === 'audit') loadAudit(); }, [tab, auditPage, auditEventFilter, loadAudit]);

  // Add consent submit
  const handleAddConsent = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddConsentLoading(true);
    setAddConsentError('');
    try {
      const consentPayload: Parameters<typeof addConsent>[0] = {
        phone: addConsentForm.phone,
        consentMethod: addConsentForm.consentMethod,
      };
      if (addConsentForm.consentText) consentPayload.consentText = addConsentForm.consentText;
      if (addConsentForm.recordingUrl) consentPayload.recordingUrl = addConsentForm.recordingUrl;
      await addConsent(consentPayload);
      setShowAddConsent(false);
      setAddConsentForm({ phone: '', consentMethod: 'web_form', consentText: '', recordingUrl: '' });
      loadConsent();
      getComplianceStats().then(setStats).catch(console.error);
    } catch (err: any) {
      setAddConsentError(err?.response?.data?.error?.message ?? 'Failed to add consent');
    } finally {
      setAddConsentLoading(false);
    }
  };

  // Add DNC submit
  const handleAddDnc = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await addToDNC(addDncForm.phone, addDncForm.reason);
      setShowAddDnc(false);
      setAddDncForm({ phone: '', reason: 'manual' });
      loadDNC();
      getComplianceStats().then(setStats).catch(console.error);
    } catch (err) { console.error(err); }
  };

  // Check DNC
  const handleCheckDnc = async () => {
    if (!checkDncPhone.trim()) return;
    const [result] = await checkDNC([checkDncPhone.trim()]);
    setCheckDncResult(result ?? null);
  };

  // Export audit log as CSV
  const exportAuditCSV = async () => {
    const result = await getAuditLog({ limit: 10000 });
    const rows = [
      ['Event', 'Phone', 'Campaign ID', 'Call SID', 'Timestamp', 'Details'],
      ...result.events.map(e => [
        e.event_type, e.phone ?? '', e.campaign_id ?? '', e.call_sid ?? '',
        e.created_at, JSON.stringify(e.details),
      ]),
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `compliance-audit-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = (total: number, limit = 50) => Math.max(1, Math.ceil(total / limit));

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="p-6 min-h-screen" style={{ background: '#0a0a0f', color: '#e5e7eb' }}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-1">
          <span className="text-2xl">🛡️</span>
          <h1 className="text-2xl font-bold text-white">Compliance Center</h1>
          <span
            className="text-xs px-2 py-1 rounded-full font-semibold"
            style={{ background: 'rgba(220,38,38,0.15)', color: '#ef4444', border: '1px solid rgba(220,38,38,0.3)' }}
          >
            REQUIRED
          </span>
        </div>
        <p className="text-sm" style={{ color: '#6b7280' }}>
          TCPA compliance management — consent tracking, DNC list, quiet hours enforcement, and audit trail.
          Violations can cost $500–$1,500 per call.
        </p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card
          label="Consent Records"
          value={statsLoading ? '…' : stats?.totalConsentRecords ?? 0}
          icon="✅"
          accent="#22c55e"
          sub="Valid & active"
        />
        <Card
          label="DNC List"
          value={statsLoading ? '…' : stats?.dncListSize ?? 0}
          icon="🚫"
          accent="#ef4444"
          sub="Do not call"
        />
        <Card
          label="Blocked This Month"
          value={statsLoading ? '…' : stats?.callsBlockedThisMonth ?? 0}
          icon="⛔"
          accent="#f59e0b"
          sub="Compliance blocks"
        />
        <Card
          label="Compliance Score"
          value={statsLoading ? '…' : `${stats?.complianceScore ?? 0}%`}
          icon="📊"
          accent="#818cf8"
          sub="Contacts with consent"
        />
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6">
        <Tab label="Consent Records" active={tab === 'consent'} onClick={() => setTab('consent')} />
        <Tab label="DNC List" active={tab === 'dnc'} onClick={() => setTab('dnc')} />
        <Tab label="Audit Log" active={tab === 'audit'} onClick={() => setTab('audit')} />
        <Tab label="Settings" active={tab === 'settings'} onClick={() => setTab('settings')} />
      </div>

      {/* ── Consent Tab ── */}
      {tab === 'consent' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Consent Records</h2>
            <div className="flex gap-2">
              <button
                onClick={() => setShowAddConsent(true)}
                className="px-4 py-2 text-sm font-medium rounded-lg transition-all"
                style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.3)' }}
              >
                + Add Consent
              </button>
            </div>
          </div>

          {showAddConsent && (
            <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)' }}>
              <div className="rounded-xl p-6 w-full max-w-md" style={{ background: '#13131f', border: '1px solid rgba(255,255,255,0.08)' }}>
                <h3 className="text-lg font-semibold text-white mb-4">Add Consent Record</h3>
                <form onSubmit={handleAddConsent} className="space-y-4">
                  <div>
                    <label className="block text-sm mb-1" style={{ color: '#9ca3af' }}>Phone Number</label>
                    <input
                      type="text"
                      value={addConsentForm.phone}
                      onChange={e => setAddConsentForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="+1XXXXXXXXXX"
                      required
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      style={{ background: '#0a0a0f', border: '1px solid rgba(255,255,255,0.1)', color: '#e5e7eb' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1" style={{ color: '#9ca3af' }}>Consent Method</label>
                    <select
                      value={addConsentForm.consentMethod}
                      onChange={e => setAddConsentForm(f => ({ ...f, consentMethod: e.target.value as any }))}
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      style={{ background: '#0a0a0f', border: '1px solid rgba(255,255,255,0.1)', color: '#e5e7eb' }}
                    >
                      <option value="web_form">Web Form</option>
                      <option value="text">Text/SMS</option>
                      <option value="verbal">Verbal</option>
                      <option value="import">Import</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm mb-1" style={{ color: '#9ca3af' }}>Consent Text (optional)</label>
                    <textarea
                      value={addConsentForm.consentText}
                      onChange={e => setAddConsentForm(f => ({ ...f, consentText: e.target.value }))}
                      rows={2}
                      placeholder="I agree to receive calls from..."
                      className="w-full px-3 py-2 rounded-lg text-sm resize-none"
                      style={{ background: '#0a0a0f', border: '1px solid rgba(255,255,255,0.1)', color: '#e5e7eb' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1" style={{ color: '#9ca3af' }}>Recording URL (optional)</label>
                    <input
                      type="url"
                      value={addConsentForm.recordingUrl}
                      onChange={e => setAddConsentForm(f => ({ ...f, recordingUrl: e.target.value }))}
                      placeholder="https://..."
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      style={{ background: '#0a0a0f', border: '1px solid rgba(255,255,255,0.1)', color: '#e5e7eb' }}
                    />
                  </div>
                  {addConsentError && <p className="text-sm" style={{ color: '#ef4444' }}>{addConsentError}</p>}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={addConsentLoading}
                      className="flex-1 py-2 rounded-lg text-sm font-medium"
                      style={{ background: '#4f46e5', color: '#fff' }}
                    >
                      {addConsentLoading ? 'Saving…' : 'Save Consent'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowAddConsent(false)}
                      className="flex-1 py-2 rounded-lg text-sm font-medium"
                      style={{ background: 'rgba(255,255,255,0.05)', color: '#9ca3af' }}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#13131f', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  {['Phone', 'Method', 'Consent Date', 'Expires', 'Status'].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-medium" style={{ color: '#6b7280' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {consentLoading ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center" style={{ color: '#6b7280' }}>Loading…</td></tr>
                ) : consentRecords.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-8 text-center" style={{ color: '#6b7280' }}>No consent records yet. Add consent before launching campaigns.</td></tr>
                ) : (
                  consentRecords.map((r, i) => {
                    const status = consentStatus(r);
                    return (
                      <tr
                        key={r.id}
                        style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? '#0a0a0f' : '#0d0d14' }}
                      >
                        <td className="px-4 py-3 font-mono text-xs" style={{ color: '#e5e7eb' }}>{r.phone}</td>
                        <td className="px-4 py-3 capitalize" style={{ color: '#9ca3af' }}>{r.consent_method.replace('_', ' ')}</td>
                        <td className="px-4 py-3" style={{ color: '#9ca3af' }}>{fmt(r.consented_at)}</td>
                        <td className="px-4 py-3" style={{ color: '#9ca3af' }}>{fmt(r.expires_at)}</td>
                        <td className="px-4 py-3">
                          <span
                            className="text-xs px-2 py-1 rounded-full font-medium"
                            style={{ background: `${status.color}20`, color: status.color }}
                          >
                            {status.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm" style={{ color: '#6b7280' }}>{consentTotal} total records</span>
            <div className="flex gap-2">
              <button
                onClick={() => setConsentPage(p => Math.max(1, p - 1))}
                disabled={consentPage === 1}
                className="px-3 py-1 text-sm rounded"
                style={{ background: 'rgba(255,255,255,0.05)', color: '#9ca3af', opacity: consentPage === 1 ? 0.4 : 1 }}
              >← Prev</button>
              <span className="px-3 py-1 text-sm" style={{ color: '#9ca3af' }}>
                {consentPage} / {totalPages(consentTotal)}
              </span>
              <button
                onClick={() => setConsentPage(p => Math.min(totalPages(consentTotal), p + 1))}
                disabled={consentPage >= totalPages(consentTotal)}
                className="px-3 py-1 text-sm rounded"
                style={{ background: 'rgba(255,255,255,0.05)', color: '#9ca3af', opacity: consentPage >= totalPages(consentTotal) ? 0.4 : 1 }}
              >Next →</button>
            </div>
          </div>
        </div>
      )}

      {/* ── DNC Tab ── */}
      {tab === 'dnc' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Do Not Call List</h2>
            <button
              onClick={() => setShowAddDnc(true)}
              className="px-4 py-2 text-sm font-medium rounded-lg"
              style={{ background: 'rgba(239,68,68,0.12)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.25)' }}
            >
              + Add to DNC
            </button>
          </div>

          {/* Check number */}
          <div
            className="rounded-xl p-4 mb-4 flex gap-3 items-end"
            style={{ background: '#13131f', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <div className="flex-1">
              <label className="block text-xs mb-1" style={{ color: '#6b7280' }}>Check a phone number</label>
              <input
                type="text"
                value={checkDncPhone}
                onChange={e => { setCheckDncPhone(e.target.value); setCheckDncResult(null); }}
                placeholder="+1XXXXXXXXXX"
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{ background: '#0a0a0f', border: '1px solid rgba(255,255,255,0.1)', color: '#e5e7eb' }}
              />
            </div>
            <button
              onClick={handleCheckDnc}
              className="px-4 py-2 text-sm font-medium rounded-lg"
              style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.25)' }}
            >
              Check
            </button>
            {checkDncResult && (
              <span
                className="px-3 py-2 text-sm font-semibold rounded-lg"
                style={{
                  background: checkDncResult.onDNC ? 'rgba(239,68,68,0.12)' : 'rgba(34,197,94,0.12)',
                  color: checkDncResult.onDNC ? '#ef4444' : '#22c55e',
                }}
              >
                {checkDncResult.onDNC ? `On DNC (${checkDncResult.reason})` : 'Not on DNC ✓'}
              </span>
            )}
          </div>

          {showAddDnc && (
            <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.6)' }}>
              <div className="rounded-xl p-6 w-full max-w-sm" style={{ background: '#13131f', border: '1px solid rgba(255,255,255,0.08)' }}>
                <h3 className="text-lg font-semibold text-white mb-4">Add to DNC</h3>
                <form onSubmit={handleAddDnc} className="space-y-4">
                  <div>
                    <label className="block text-sm mb-1" style={{ color: '#9ca3af' }}>Phone Number</label>
                    <input
                      type="text"
                      value={addDncForm.phone}
                      onChange={e => setAddDncForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="+1XXXXXXXXXX"
                      required
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      style={{ background: '#0a0a0f', border: '1px solid rgba(255,255,255,0.1)', color: '#e5e7eb' }}
                    />
                  </div>
                  <div>
                    <label className="block text-sm mb-1" style={{ color: '#9ca3af' }}>Reason</label>
                    <select
                      value={addDncForm.reason}
                      onChange={e => setAddDncForm(f => ({ ...f, reason: e.target.value as any }))}
                      className="w-full px-3 py-2 rounded-lg text-sm"
                      style={{ background: '#0a0a0f', border: '1px solid rgba(255,255,255,0.1)', color: '#e5e7eb' }}
                    >
                      <option value="manual">Manual</option>
                      <option value="opted_out">Opted Out</option>
                      <option value="requested">Requested Removal</option>
                      <option value="invalid">Invalid Number</option>
                    </select>
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button type="submit" className="flex-1 py-2 rounded-lg text-sm font-medium" style={{ background: '#dc2626', color: '#fff' }}>
                      Add to DNC
                    </button>
                    <button type="button" onClick={() => setShowAddDnc(false)} className="flex-1 py-2 rounded-lg text-sm font-medium" style={{ background: 'rgba(255,255,255,0.05)', color: '#9ca3af' }}>
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#13131f', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  {['Phone', 'Reason', 'Date Added'].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-medium" style={{ color: '#6b7280' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dncLoading ? (
                  <tr><td colSpan={3} className="px-4 py-8 text-center" style={{ color: '#6b7280' }}>Loading…</td></tr>
                ) : dncEntries.length === 0 ? (
                  <tr><td colSpan={3} className="px-4 py-8 text-center" style={{ color: '#6b7280' }}>No DNC entries.</td></tr>
                ) : (
                  dncEntries.map((e, i) => (
                    <tr
                      key={e.id}
                      style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? '#0a0a0f' : '#0d0d14' }}
                    >
                      <td className="px-4 py-3 font-mono text-xs" style={{ color: '#e5e7eb' }}>{e.phone}</td>
                      <td className="px-4 py-3 capitalize" style={{ color: '#9ca3af' }}>{e.reason.replace('_', ' ')}</td>
                      <td className="px-4 py-3" style={{ color: '#9ca3af' }}>{fmt(e.added_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4">
            <span className="text-sm" style={{ color: '#6b7280' }}>{dncTotal} total entries</span>
            <div className="flex gap-2">
              <button onClick={() => setDncPage(p => Math.max(1, p - 1))} disabled={dncPage === 1} className="px-3 py-1 text-sm rounded" style={{ background: 'rgba(255,255,255,0.05)', color: '#9ca3af', opacity: dncPage === 1 ? 0.4 : 1 }}>← Prev</button>
              <span className="px-3 py-1 text-sm" style={{ color: '#9ca3af' }}>{dncPage} / {totalPages(dncTotal)}</span>
              <button onClick={() => setDncPage(p => Math.min(totalPages(dncTotal), p + 1))} disabled={dncPage >= totalPages(dncTotal)} className="px-3 py-1 text-sm rounded" style={{ background: 'rgba(255,255,255,0.05)', color: '#9ca3af', opacity: dncPage >= totalPages(dncTotal) ? 0.4 : 1 }}>Next →</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Audit Log Tab ── */}
      {tab === 'audit' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Compliance Audit Log</h2>
            <button
              onClick={exportAuditCSV}
              className="px-4 py-2 text-sm font-medium rounded-lg"
              style={{ background: 'rgba(99,102,241,0.15)', color: '#818cf8', border: '1px solid rgba(99,102,241,0.25)' }}
            >
              ↓ Export CSV
            </button>
          </div>

          {/* Filter */}
          <div className="mb-4">
            <select
              value={auditEventFilter}
              onChange={e => { setAuditEventFilter(e.target.value); setAuditPage(1); }}
              className="px-3 py-2 rounded-lg text-sm"
              style={{ background: '#13131f', border: '1px solid rgba(255,255,255,0.08)', color: '#e5e7eb' }}
            >
              <option value="">All Events</option>
              {Object.entries(EVENT_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: '#13131f', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                  {['Event', 'Phone', 'Timestamp', 'Details'].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-medium" style={{ color: '#6b7280' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {auditLoading ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center" style={{ color: '#6b7280' }}>Loading…</td></tr>
                ) : events.length === 0 ? (
                  <tr><td colSpan={4} className="px-4 py-8 text-center" style={{ color: '#6b7280' }}>No events found.</td></tr>
                ) : (
                  events.map((ev, i) => (
                    <tr
                      key={ev.id}
                      style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: i % 2 === 0 ? '#0a0a0f' : '#0d0d14' }}
                    >
                      <td className="px-4 py-3">
                        <span
                          className="text-xs px-2 py-1 rounded-full font-medium"
                          style={{
                            background: ev.event_type.includes('blocked') || ev.event_type.includes('opt_out') ? 'rgba(239,68,68,0.12)' : 'rgba(99,102,241,0.12)',
                            color: ev.event_type.includes('blocked') || ev.event_type.includes('opt_out') ? '#ef4444' : '#818cf8',
                          }}
                        >
                          {EVENT_LABELS[ev.event_type] ?? ev.event_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs" style={{ color: '#9ca3af' }}>{ev.phone ?? '—'}</td>
                      <td className="px-4 py-3 text-xs" style={{ color: '#6b7280' }}>{fmtTime(ev.created_at)}</td>
                      <td className="px-4 py-3 text-xs max-w-xs truncate" style={{ color: '#6b7280' }}>
                        {Object.keys(ev.details).length > 0 ? JSON.stringify(ev.details) : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4">
            <span className="text-sm" style={{ color: '#6b7280' }}>{auditTotal} total events</span>
            <div className="flex gap-2">
              <button onClick={() => setAuditPage(p => Math.max(1, p - 1))} disabled={auditPage === 1} className="px-3 py-1 text-sm rounded" style={{ background: 'rgba(255,255,255,0.05)', color: '#9ca3af', opacity: auditPage === 1 ? 0.4 : 1 }}>← Prev</button>
              <span className="px-3 py-1 text-sm" style={{ color: '#9ca3af' }}>{auditPage} / {totalPages(auditTotal)}</span>
              <button onClick={() => setAuditPage(p => Math.min(totalPages(auditTotal), p + 1))} disabled={auditPage >= totalPages(auditTotal)} className="px-3 py-1 text-sm rounded" style={{ background: 'rgba(255,255,255,0.05)', color: '#9ca3af', opacity: auditPage >= totalPages(auditTotal) ? 0.4 : 1 }}>Next →</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Settings Tab ── */}
      {tab === 'settings' && (
        <div className="max-w-2xl space-y-6">
          <h2 className="text-lg font-semibold text-white">Compliance Settings</h2>

          {/* Quiet Hours */}
          <div className="rounded-xl p-5" style={{ background: '#13131f', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-medium text-white">Quiet Hours Enforcement</div>
                <div className="text-xs mt-0.5" style={{ color: '#6b7280' }}>
                  Block calls before 8 AM / after 9 PM local time. Sun: 1 PM–9 PM only. (TCPA required)
                </div>
              </div>
              <div
                className="relative w-11 h-6 rounded-full cursor-not-allowed"
                style={{ background: settings.quietHoursEnabled ? '#4f46e5' : '#374151' }}
                title="Quiet hours cannot be disabled (TCPA requirement)"
              >
                <div
                  className="absolute top-1 w-4 h-4 rounded-full transition-all"
                  style={{
                    background: '#fff',
                    left: settings.quietHoursEnabled ? '1.375rem' : '0.25rem',
                  }}
                />
              </div>
            </div>
            <p className="text-xs" style={{ color: '#f59e0b' }}>
              ⚠️ Quiet hours enforcement is always-on. Disabling requires Pro plan with legal attestation.
            </p>
          </div>

          {/* Consent Requirement */}
          <div className="rounded-xl p-5" style={{ background: '#13131f', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-medium text-white">Require Prior Written Consent</div>
                <div className="text-xs mt-0.5" style={{ color: '#6b7280' }}>
                  Jan 2025 FCC one-to-one consent rule: each company needs separate written consent.
                </div>
              </div>
              <div
                className="relative w-11 h-6 rounded-full cursor-not-allowed"
                style={{ background: settings.consentRequired ? '#4f46e5' : '#374151' }}
              >
                <div
                  className="absolute top-1 w-4 h-4 rounded-full"
                  style={{ background: '#fff', left: settings.consentRequired ? '1.375rem' : '0.25rem' }}
                />
              </div>
            </div>
            <p className="text-xs" style={{ color: '#f59e0b' }}>
              ⚠️ Consent requirement is always-on under the Jan 2025 FCC rules.
            </p>
          </div>

          {/* AI Disclosure */}
          <div className="rounded-xl p-5" style={{ background: '#13131f', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="font-medium text-white mb-1">AI Disclosure Opener</div>
            <div className="text-xs mb-3" style={{ color: '#6b7280' }}>
              The AI will say this at the start of every call to identify itself as AI (required by CallPilot policy).
            </div>
            <textarea
              value={settings.aiDisclosure}
              onChange={e => setSettings(s => ({ ...s, aiDisclosure: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 rounded-lg text-sm resize-none"
              style={{ background: '#0a0a0f', border: '1px solid rgba(255,255,255,0.1)', color: '#e5e7eb' }}
            />
          </div>

          {/* Opt-out Keywords */}
          <div className="rounded-xl p-5" style={{ background: '#13131f', border: '1px solid rgba(255,255,255,0.06)' }}>
            <div className="font-medium text-white mb-1">Opt-out Keywords</div>
            <div className="text-xs mb-3" style={{ color: '#6b7280' }}>
              When the caller says any of these phrases, the call is terminated and the number is added to DNC immediately.
            </div>
            <input
              type="text"
              value={settings.optOutKeywords}
              onChange={e => setSettings(s => ({ ...s, optOutKeywords: e.target.value }))}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ background: '#0a0a0f', border: '1px solid rgba(255,255,255,0.1)', color: '#e5e7eb' }}
            />
            <p className="text-xs mt-2" style={{ color: '#6b7280' }}>Comma-separated keywords/phrases.</p>
          </div>

          <button
            onClick={() => alert('Settings saved (persisted locally — backend settings endpoint coming soon)')}
            className="px-6 py-2 rounded-lg text-sm font-medium"
            style={{ background: '#4f46e5', color: '#fff' }}
          >
            Save Settings
          </button>
        </div>
      )}
    </div>
  );
};

export default ComplianceCenter;
